import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import {AccountService} from "./account.service";
import {Resource} from "../models/resource";
import {Service} from "../models/service";

@Injectable({ providedIn: 'root' })
export class ResourceService {
    constructor(
        private router: Router,
        private http: HttpClient,
        private accountService: AccountService
    ) {

    }

    getAll() {
        return this.http.get<Resource[]>(`${environment.apiEndpoint}/v1/resources`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getFiscalPrinters() {
        return this.http.get<Resource[]>(`${environment.apiEndpoint}/v1/resources?filters[isFiscalPrinter]=true`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getAllInUse() {
        return this.http.get<Resource[]>(`${environment.apiEndpoint}/v1/resources/in/use`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getAllByPriority() {
        return this.http.get<Resource[]>(`${environment.apiEndpoint}/v1/resources?sortCol=calendarPriority&sortDir=ASC`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getAllByCategory(id: string) {
        return this.http.get<Resource[]>(`${environment.apiEndpoint}/v1/resources?filters[category]=` + id, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getById(id: string) {
        return this.http.get<Resource>(`${environment.apiEndpoint}/v1/resources/${id}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    update(id, params) {
        return this.http.put(`${environment.apiEndpoint}/v1/resources/${id}`, params, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
            .pipe(map(x => {
                return x;
            }));
    }

    create(params) {
        return this.http.post(`${environment.apiEndpoint}/v1/resources`, params, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
            .pipe(map(x => {
                return x;
            }));
    }

    getManyServiceDeviceById(id: string[]) {
        return this.http.post<any[]>(`${environment.apiEndpoint}/v1/resources/from-service`, {
            ids: id
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getServiceDeviceById(id: string) {
        return this.http.get<any>(`${environment.apiEndpoint}/v1/resources/${id}/from-service`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getServiceDeviceModulesById(id: string) {
        return this.http.get<any>(`${environment.apiEndpoint}/v1/resources/${id}/modules-from-service`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    openSlot(id: string, slotNumber: string) {
        return this.http.post<any[]>(`${environment.apiEndpoint}/v1/resources/${id}/open-slots`, {
            slots: {
                slot1: slotNumber === '1',
                slot2: slotNumber === '2',
                slot3: slotNumber === '3',
                slot4: slotNumber === '4',
                slot5: slotNumber === '5',
                slot6: slotNumber === '6',
            }
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    // Device Statistics Methods
    getDeviceStats(id: string, offset: number = 0, limit: number = 100) {
        return this.http.get<any>(`${environment.apiEndpoint}/v1/resources/${id}/device-stats?offset=${offset}&limit=${limit}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getDeviceStatsDaily(id: string, startDate: string, endDate: string) {
        return this.http.get<any>(`${environment.apiEndpoint}/v1/resources/${id}/device-stats/daily?startDate=${startDate}&endDate=${endDate}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getOperatorDeviceStats(startDate: string, endDate: string, offset: number = 0, limit: number = 100) {
        return this.http.get<any>(`${environment.apiEndpoint}/v1/resources/operator/device-stats?startDate=${startDate}&endDate=${endDate}&offset=${offset}&limit=${limit}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getOperatorDeviceStatsDaily(startDate: string, endDate: string) {
        return this.http.get<any>(`${environment.apiEndpoint}/v1/resources/operator/device-stats/daily?startDate=${startDate}&endDate=${endDate}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }
}
